/* W8.7 — Sensitivity Lab : simulation STRICTEMENT stateless (/decisions/simulate).
 * Rien n'est persisté — on explore les probabilités/payoffs/coûts en direct. */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { FlaskConical, Plus, Trash2 } from "lucide-react";

const fmtCHF = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v);
const METHODS = ["expert", "historical", "model", "market", "autre"];
const VERDICT_STYLE = { PURSUE: "border-[#1D9E75]/50 text-[#1D9E75] bg-[#1D9E75]/10", HOLD: "border-[#EF9F27]/50 text-[#EF9F27] bg-[#EF9F27]/10", PASS: "border-[#E24B4A]/50 text-[#E24B4A] bg-[#E24B4A]/10" };
const DEFAULTS = [
  { label: "Succès", payoff_chf: 100000, probability: { value: 0.6, source: "Hypothèse Lab", method: "expert", confidence: 0.5 } },
  { label: "Échec", payoff_chf: -50000, probability: { value: 0.4, source: "Hypothèse Lab", method: "expert", confidence: 0.5 } },
];

export function DecisionLab() {
  const [scenarios, setScenarios] = useState(DEFAULTS);
  const [cost, setCost] = useState(5000);
  const [coc, setCoc] = useState(0);
  const [capital, setCapital] = useState("");
  const [marketP, setMarketP] = useState("");
  const [result, setResult] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const timer = useRef(null);

  useEffect(() => { api.get("/workspace/decisions").then((r) => setDecisions(r.data.items)).catch(() => {}); }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = {
        title: "Simulation Lab", cost_chf: Number(cost) || 0,
        cost_of_capital_chf: Number(coc) || 0,
        capital_at_risk_chf: capital === "" ? null : Number(capital),
        market_implied_probability: marketP === "" ? null : Number(marketP),
        scenarios: scenarios.map((s) => ({ ...s, payoff_chf: Number(s.payoff_chf) || 0, probability: { ...s.probability, value: Number(s.probability.value), confidence: Number(s.probability.confidence), source: s.probability.source || "Hypothèse Lab" } })),
      };
      api.post("/workspace/decisions/simulate", payload).then((r) => setResult(r.data)).catch((e) => { setResult(null); if (e?.response?.status !== 422) notify.error(fmtError(e)); });
    }, 350);
    return () => clearTimeout(timer.current);
  }, [scenarios, cost, coc, capital, marketP]);

  const upd = (i, patch, probPatch) => setScenarios((arr) => arr.map((x, j) => j === i ? { ...x, ...patch, probability: { ...x.probability, ...(probPatch || {}) } } : x));
  const loadDecision = (id) => {
    const d = decisions.find((x) => x.id === id);
    if (!d) return;
    setScenarios((d.scenarios || []).map((s) => ({ label: s.label, payoff_chf: s.payoff_chf, probability: { value: s.probability.value, source: s.probability.source, method: s.probability.method, confidence: s.probability.confidence } })));
    setCost(d.cost_chf ?? 0); setCoc(d.cost_of_capital_chf ?? 0);
    setCapital(d.capital_at_risk_chf ?? ""); setMarketP(d.market_implied_probability ?? "");
    notify.success("Décision chargée dans le Lab — la décision réelle n'est PAS modifiée.");
  };

  const eco = result?.economics || {};
  const th = result?.thresholds || {};
  return (
    <div className="space-y-3" data-testid="decision-lab">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-[#7F77DD]/30 text-[#7F77DD] bg-[#7F77DD]/5 flex items-center gap-1.5">
        <FlaskConical className="w-3.5 h-3.5" /> Simulation stateless — rien n'est persisté, aucune décision réelle n'est touchée.
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <select onChange={(e) => e.target.value && loadDecision(e.target.value)} defaultValue="" className="noam-input text-[10px] w-64" data-testid="lab-load-decision">
          <option value="">Charger une décision existante (copie locale)…</option>
          {decisions.map((d) => <option key={d.id} value={d.id}>{d.title} ({d.status})</option>)}
        </select>
        <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="noam-input text-[10px] w-28" title="Coûts TX (CHF)" placeholder="Coûts TX" data-testid="lab-cost" />
        <input type="number" value={coc} onChange={(e) => setCoc(e.target.value)} className="noam-input text-[10px] w-28" title="Coût du capital (CHF)" placeholder="Coût capital" />
        <input type="number" value={capital} onChange={(e) => setCapital(e.target.value)} className="noam-input text-[10px] w-28" title="Capital exposé (CHF)" placeholder="Capital exposé" />
        <input type="number" step="0.05" min="0" max="1" value={marketP} onChange={(e) => setMarketP(e.target.value)} className="noam-input text-[10px] w-24" title="Probabilité implicite marché (0-1)" placeholder="P. marché" />
      </div>
      <div className="space-y-1.5">
        {scenarios.map((s, i) => (
          <div key={i} className="p-2 rounded bg-white/[0.03] space-y-1" data-testid={`lab-scenario-${i}`}>
            <div className="flex gap-1.5 items-center flex-wrap">
              <input value={s.label} onChange={(e) => upd(i, { label: e.target.value })} className="noam-input text-[10px] w-40" placeholder="Scénario…" />
              <span className="text-[10px] font-mono text-[#7F77DD] w-10 text-right">{Math.round(s.probability.value * 100)}%</span>
              <input type="range" min="0" max="1" step="0.01" value={s.probability.value} onChange={(e) => upd(i, {}, { value: e.target.value })} className="flex-1 min-w-[120px] accent-[#7F77DD]" data-testid={`lab-prob-slider-${i}`} />
              <input type="number" value={s.payoff_chf} onChange={(e) => upd(i, { payoff_chf: e.target.value })} className="noam-input text-[10px] w-28" title="Payoff CHF" data-testid={`lab-payoff-${i}`} />
              <input value={s.probability.source} onChange={(e) => upd(i, {}, { source: e.target.value })} placeholder="Source" className="noam-input text-[10px] w-32" data-testid={`lab-source-${i}`} />
              <select value={s.probability.method} onChange={(e) => upd(i, {}, { method: e.target.value })} className="noam-input text-[10px] w-28" data-testid={`lab-method-${i}`}>
                {METHODS.map((m) => <option key={m}>{m}</option>)}
              </select>
              <input type="number" step="0.1" min="0" max="1" value={s.probability.confidence} onChange={(e) => upd(i, {}, { confidence: e.target.value })} className="noam-input text-[10px] w-16" title="Confiance (0-1)" data-testid={`lab-confidence-${i}`} />
              <button onClick={() => setScenarios((x) => x.filter((_, j) => j !== i))} className="btn-ghost p-1 text-[#E24B4A]"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        <button onClick={() => setScenarios((s) => [...s, { label: "", payoff_chf: 0, probability: { value: 0.2, source: "Hypothèse Lab", method: "expert", confidence: 0.5 } }])} className="btn-ghost text-[10px]"><Plus className="w-3 h-3" /> Scénario</button>
      </div>
      {result && (
        <div className="noam-card p-3 space-y-2" data-testid="lab-result">
          <div className="flex gap-4 text-xs text-zinc-400 flex-wrap items-center">
            <span>EV <b className="text-zinc-100">{fmtCHF(eco.ev_chf)}</b></span>
            <span>AdjEV <b className={eco.adjusted_ev_chf >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"} data-testid="lab-adjev">{fmtCHF(eco.adjusted_ev_chf)}</b></span>
            <span>P(succès) <b className="text-zinc-100">{Math.round((eco.p_success || 0) * 100)}%</b></span>
            <span>Confiance <b className="text-zinc-100">{Math.round((eco.confidence_avg || 0) * 100)}%</b></span>
            <span>Downside <b className="text-[#E24B4A]">{fmtCHF(eco.downside_chf)}</b></span>
            {eco.edge != null && <span>Edge <b className={eco.edge >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{eco.edge >= 0 ? "+" : ""}{Math.round(eco.edge * 100)} pts</b></span>}
            {eco.capital_efficiency != null && <span>Cap. eff. <b className="text-zinc-100">{eco.capital_efficiency}</b></span>}
            {result.recommendation && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${VERDICT_STYLE[result.recommendation]}`} data-testid="lab-recommendation">{result.recommendation}</span>}
          </div>
          {(eco.flags || []).length > 0 && (
            <div className="text-[10px] text-[#EF9F27]" data-testid="lab-flags">⚠ {eco.flags.join(" · ")}</div>
          )}
          <div className="text-[10px] text-zinc-400 space-y-0.5">
            <div className="uppercase tracking-widest text-zinc-400 font-semibold">Qu'est-ce qui doit être vrai pour que la décision change ?</div>
            {th.min_p_success_for_positive_ev != null && <div>Break-even P(succès) : <b className="text-zinc-300">{Math.round(th.min_p_success_for_positive_ev * 100)}%</b></div>}
            <div>Surcoût max avant AdjEV négative : <b className="text-zinc-300">{fmtCHF(th.max_extra_cost_before_negative_chf)}</b></div>
            <div>Coût du capital max supportable : <b className="text-zinc-300">{fmtCHF(th.max_cost_of_capital_chf)}</b></div>
          </div>
          {(eco.factors || []).length > 0 && (
            <div className="text-[10px] text-zinc-500 space-y-0.5">
              <div className="uppercase tracking-widest text-zinc-600">Contributions p×payoff</div>
              {eco.factors.map((f, i) => (
                <div key={i} className="flex gap-2"><span className="flex-1 truncate">{f.scenario}</span><span className="font-mono text-zinc-300">{fmtCHF(f.contribution_chf)}</span></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
